<template>
  <div class="modal is-active">
    <div class="modal-background" />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Add New User
        </p>
        <button
          id="modal-close-btn"
          type="button"
          class="delete"
          aria-label="close"
          @click.prevent="$emit('close')"
        />
      </header>
      <section class="modal-card-body">
        <div class="columns is-multiline">
          <div class="column is-12  has-text-centered">
            <validation-observer
              ref="profile-fields"
              tag="form"
            >
              <profile
                v-bind="{ type: 'new-user', formTitle: 'Filer Profile', formDesc: '' }"
                @profile="userProfile = $event"
              />
            </validation-observer>
            <validation-observer
              ref="login-info-fields"
              tag="form"
            >
              <login-info-admin
                @loginInfo="loginInfo = $event"
              />
            </validation-observer>
            <positions
              v-bind="{ type: 'new-user' }"
              @positions="positions = $event"
              @hasPositions="hasPositions = $event"
              @hasPrimaryPosition="hasPrimaryPosition = $event"
              @hasPositionsReady="hasPositionsReady = $event"
            />
            <validation-observer
              ref="statement-requirements-fields"
              tag="form"
            >
              <statement-requirements
                v-bind="{ type: 'new-user', required: false }"
                @requiredStatements="requiredStatements = $event"
              />
            </validation-observer>
            <validation-observer
              v-if="adminProfile.sysAdmin === 'Y'"
              ref="system-access-fields"
              tag="form"
            >
              <system-access
                v-bind="{ type: 'new-user' }"
                @systemAccessRoles="sysAccessRoles = $event"
              />
            </validation-observer>
            <dept-access
              v-if="isDeptHeadOnly"
              @deptAccess="deptAccess = $event"
            />
            <hr>
            <user-access-status
              v-bind="{ type: 'new-user' }"
              @userAccessStatuses="userAccessStatuses = $event"
            />
          </div>
          <div class="column is-12 has-text-centered">
            <button
              id="register-btn"
              type="button"
              class="button is-primary"
              :class="{ 'is-loading' : isSaving }"
              :disabled="isSaving"
              @click="registerUser"
            >
              Register New User
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
<script>
import Profile from '@/components/Profile';
import Positions from '@/components/Positions';
import LoginInfoAdmin from '@/components/LoginInfoAdmin';
import { controlValueHelpers } from '@/mixins/controlValue';
import StatementRequirements from '@/components/StatementRequirements';
import DeptAccess from '@/components/DeptAccess';
import SystemAccess from '@/components/SystemAccess';
import UserAccessStatus from '@/components/UserAccessStatus';

export default {
  components: {
    Profile,
    Positions,
    LoginInfoAdmin,
    StatementRequirements,
    SystemAccess,
    UserAccessStatus,
    DeptAccess,
  },
  mixins: [
    controlValueHelpers,
  ],
  data() {
    return {
      userProfile: {},
      loginInfo: {},
      isSaving: false,
      positions: [],
      hasPositions: false,
      hasPrimaryPosition: true,
      requiredStatements: {},
      deptAccess: [],
      sysAccessRoles: {},
    };
  },
  computed: {
    isDeptHeadOnly () {
      if (this.sysAccessRoles.deptHead === 'Y' && this.sysAccessRoles.sysAdmin === 'N') {
        return true;
      }
      return false;
    },
    adminProfile () {
      return this.$store.state.user.profile;
    },
  },
  async mounted () {
    this.ready = true;
  },
  methods: {
    async registerUser () {

      let isValidProfile = await this.$refs['profile-fields'].validate();
      let isValidLoginInfo = await this.$refs['login-info-fields'].validate();
      let isValidStatements = await this.$refs['statement-requirements-fields'].validate();

      this.isSaving = true;

      if (
        !isValidProfile ||
        !isValidLoginInfo ||
        !isValidStatements ||
        (this.isDeptHeadOnly && this.deptAccess.length === 0) ||
        !this.hasPositions ||
        !this.hasPrimaryPosition
      ) {
        let warnings = [];
        warnings.push("There are some validation issues.");
        if (!this.hasPrimaryPosition) {
          warnings.push("The user must have 1 primary position. Edit the primary position (which the user currently holds) and check the \"Primary Position\" box.");
        }
        warnings.push("Please review your information and try again.");
        this.$warning(warnings.join(' '));
        this.isSaving = false;
        return false;
      }

      let newProfile = { ...this.userProfile, ...this.loginInfo, ...this.requiredStatements, ...this.sysAccessRoles, ...this.userAccessStatuses };

      if (newProfile.username === '') {
        newProfile.username = newProfile.emailAddress;
      }

      newProfile.selfRegister = 'Y';

      let positions = this.positions.slice(0);
      positions.map(position => {
        delete position.id;
      });

      newProfile.positions = positions;

      this.$store.dispatch('user/registerNewAdminUser', { fields: newProfile }).then((response) => {
        if (response.status === 201) {
          if (this.isDeptHeadOnly) {
            this.$store.dispatch('user/saveUserDepts', {
              agencyIds: this.deptAccess,
              userId: this.userProfile.id,
            }).then(() => {
              this.$success("New user created successfully. If checked as 'active', the new user will receive an e-mail with instructions to set a new password'");
              this.$emit('close');
              this.isSaving = false;
            });
          } else {
            this.$success("New user created successfully. If checked as 'active', the new user will receive an e-mail with instructions to set a new password'");
            this.$emit('close');
            this.isSaving = false;
          }
        } else {
          this.$warning("There was an issue creating your account. Please try again or contact support.");
        }
      }).catch(() => {
        this.isSaving = false;
        this.$warning("There was an error creating a new user. Please contact support.");
      });

    },
  },
};
</script>
